import { Injectable } from '@angular/core';
import { Apollo, gql } from 'apollo-angular';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

export type DomainStatus = 'pending' | 'issued' | 'failed';

export interface DomainValidationRecord {
    name: string;
    value: string;
    type: string;
}

export interface Domain {
    _id: string;
    domain: string;
    redirectWww: boolean;
    certificateArn: string;
    ownsCert: boolean;
    status: DomainStatus;
    statusError?: string | null;
    lastCheckedAt?: string | null;
    validationRecords: DomainValidationRecord[];
    createdAt?: string;
    updatedAt?: string;
}

const DOMAIN_FIELDS = gql`
    fragment domainFields on Domain {
        _id
        domain
        redirectWww
        certificateArn
        ownsCert
        status
        statusError
        lastCheckedAt
        validationRecords {
            name
            value
            type
        }
        createdAt
        updatedAt
    }
`;

const DOMAINS_QUERY = gql`
    ${DOMAIN_FIELDS}
    query Domains {
        domains {
            ...domainFields
        }
    }
`;

const DOMAINS_ISSUED_QUERY = gql`
    ${DOMAIN_FIELDS}
    query DomainsIssued {
        domainsIssued {
            ...domainFields
        }
    }
`;

const CREATE_DOMAIN = gql`
    ${DOMAIN_FIELDS}
    mutation CreateDomain($input: CreateDomainInput!) {
        createDomain(input: $input) {
            ...domainFields
        }
    }
`;

const REFRESH_DOMAIN = gql`
    ${DOMAIN_FIELDS}
    mutation RefreshDomainStatus($id: ID!) {
        refreshDomainStatus(id: $id) {
            ...domainFields
        }
    }
`;

const DELETE_DOMAIN = gql`
    mutation DeleteDomain($id: ID!) {
        deleteDomain(id: $id)
    }
`;

const SITE_IP_QUERY = gql`
    query SiteIpAddress {
        siteIpAddress
    }
`;

@Injectable({ providedIn: 'root' })
export class DomainsService {
    constructor(private apollo: Apollo) {}

    list(): Observable<Domain[]> {
        return this.apollo
            .query<{ domains: Domain[] }>({ query: DOMAINS_QUERY, fetchPolicy: 'network-only' })
            .pipe(map((r) => r.data?.domains ?? []));
    }

    listIssued(): Observable<Domain[]> {
        return this.apollo
            .query<{ domainsIssued: Domain[] }>({ query: DOMAINS_ISSUED_QUERY, fetchPolicy: 'network-only' })
            .pipe(map((r) => r.data?.domainsIssued ?? []));
    }

    create(domain: string, redirectWww: boolean): Observable<Domain> {
        return this.apollo
            .mutate<{ createDomain: Domain }>({
                mutation: CREATE_DOMAIN,
                variables: { input: { domain, redirectWww } },
            })
            .pipe(map((r) => r.data!.createDomain));
    }

    refresh(id: string): Observable<Domain> {
        return this.apollo
            .mutate<{ refreshDomainStatus: Domain }>({
                mutation: REFRESH_DOMAIN,
                variables: { id },
            })
            .pipe(map((r) => r.data!.refreshDomainStatus));
    }

    delete(id: string): Observable<boolean> {
        return this.apollo
            .mutate<{ deleteDomain: boolean }>({
                mutation: DELETE_DOMAIN,
                variables: { id },
            })
            .pipe(map((r) => r.data!.deleteDomain));
    }

    siteIpAddress(): Observable<string | null> {
        return this.apollo
            .query<{ siteIpAddress: string | null }>({ query: SITE_IP_QUERY, fetchPolicy: 'network-only' })
            .pipe(map((r) => r.data?.siteIpAddress ?? null));
    }
}
